import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText } from "vue"

const _hoisted_1 = { class: "container-fluid" }
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "col-12 col-lg-8" }
const _hoisted_4 = { class: "d-flex justify-content-end gap-2 mt-4" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = {
  key: 0,
  class: "spinner-border spinner-border-sm me-2"
}
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["innerHTML"]
const _hoisted_9 = {
  key: 0,
  class: "card mt-3"
}
const _hoisted_10 = { class: "card-header d-flex align-items-center justify-content-between" }
const _hoisted_11 = {
  key: 0,
  class: "text-muted"
}
const _hoisted_12 = { class: "card-body" }
const _hoisted_13 = {
  key: 0,
  class: "text-center text-muted py-3"
}
const _hoisted_14 = ["innerHTML"]
const _hoisted_15 = { class: "col-12 col-lg-4 mt-4 mt-lg-0" }
const _hoisted_16 = { class: "card" }
const _hoisted_17 = { class: "card-body" }
const _hoisted_18 = ["value"]
const _hoisted_19 = { class: "mt-4" }
const _hoisted_20 = { class: "d-flex align-items-center justify-content-between mb-2" }
const _hoisted_21 = {
  key: 0,
  class: "text-muted"
}
const _hoisted_22 = { key: 0 }
const _hoisted_23 = ["onClick"]
const _hoisted_24 = {
  key: 1,
  class: "text-muted"
}
const _hoisted_25 = { class: "mt-4" }

import { ref, computed, watch, onMounted } from 'vue';
import apiService from '@/services/apiService';
import BaseForm from '@/components/commen/modal/BaseForm.vue';
import { useRoute, useRouter } from 'vue-router';

type Option = { _id: string; title: string };


export default /*@__PURE__*/_defineComponent({
  __name: 'PromptFormNew',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const createPrompt = () => router.push('/prompts');
const isEdit = computed(() => !!route.query.id);

// ---------- Model ----------
const formData = ref({
  title: '',
  description: '',
  prompt_category_id: '',
  base_prompt_id: '',        // not shown
  workspace_module_id: '',   // not shown
   idea: '',
});
function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
// ---------- Options ----------
const categories = ref<Option[]>([]);
const promptVariables = ref<Array<{ _id: string; title: string; description: string }>>([]);

// ---------- Fields (response removed) ----------
const formFields = computed(() => [
  { key: 'title', label: 'Title', type: 'text', required: true },
  {
    key: 'description',
    label: 'Prompt',
    type: 'textarea',
    required: true,
    placeholder: 'Write prompt with {variables}',
    rows: 10,
  },
]);
const previewTick = ref(0);
const rerenderPreview = () => { previewTick.value++; };
// ---------- Variable highlighting preview ----------
const resolvedPrompt = computed(() => {
  let desc = formData.value.description || '';
  const safeIdea = escapeHtml(formData.value.idea || '');
  desc = desc.replace(/\{idea\}/gi, `<mark>${safeIdea}</mark>`);
for (const v of promptVariables.value) {
    if (/^idea$/i.test(v.title)) continue;            // <-- skip Idea here
    const regex = new RegExp(`\\{${escapeRegExp(v.title)}\\}`, 'g');
    desc = desc.replace(regex, `<mark>${escapeHtml(v.description ?? '')}</mark>`);
  }
  
  return desc;
});

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ---------- Insert variable at caret ----------
const insertVariable = (varName: string) => {
  const el = document.getElementById('baseform-description') as HTMLTextAreaElement | null;
  if (!el) {
    formData.value.description = (formData.value.description || '') + `{${varName}}`;
    return;
  }
  const start = el.selectionStart ?? formData.value.description.length;
  const end = el.selectionEnd ?? formData.value.description.length;
  const before = formData.value.description.slice(0, start);
  const after = formData.value.description.slice(end);
  formData.value.description = before + `{${varName}}` + after;

  requestAnimationFrame(() => {
    const pos = start + varName.length + 2;
    el.focus();
    el.setSelectionRange(pos, pos);
  });
};

// ---------- Loaders ----------
const loadCategories = async () => {
  const res = (await apiService.get('/common/prompt-categories')) || [];
  categories.value = res.data;
};

watch(
  () => formData.value.prompt_category_id,
  async (catId) => {
    if (catId) {
      promptVariables.value =
        (await apiService.get(`/common/prompt-variables-filter?prompt_category_id=${catId}`)) || [];
    } else {
      promptVariables.value = [];
    }
  }
);

// ---------- Check Response (OpenAI preview) ----------
const isCheckingResponse = ref(false);
const responseHtml = ref<string>('');
const lastCheckedAt = ref<string>('');

const canCheckResponse = computed(() =>
  formData.value.title.trim() &&
  formData.value.prompt_category_id.trim() &&
  formData.value.description.trim()
);
function unwrapCodeFence(s) {
  if (!s) return "";

  // Regex strips ```html ... ``` or ``` ... ```
  const m = s.match(/^```(?:\w+)?\s*([\s\S]*?)\s*```$/i);
  let clean = m ? m[1].trim() : s;

  // Unescape any \n, \" etc.
  try {
    clean = JSON.parse(`"${clean.replace(/"/g, '\\"')}"`);
  } catch {
    clean = clean.replace(/\\n/g, '\n'); 
  }

  return clean;
}

const evaluatedPromptForLLM = computed(() => {
  let txt = formData.value.description || '';

  // 1) Replace {idea} case-insensitively with the user's idea
  txt = txt.replace(/\{idea\}/gi, formData.value.idea || '');

  // 2) Replace all other variables with their descriptions
  for (const v of promptVariables.value) {
    if (/^idea$/i.test(v.title)) continue; // don't overwrite the user's idea
    const re = new RegExp(`\\{${escapeRegExp(v.title)}\\}`, 'gi');
    txt = txt.replace(re, (v.description ?? '').toString());
  }

  return txt;
});
const handleCheckResponse = async () => {
  if (!canCheckResponse.value || isCheckingResponse.value) return;

  // re-render immediately on click
  rerenderPreview();

  isCheckingResponse.value = true;
  responseHtml.value = '';

  try {
    const payload = {
      title: formData.value.title,
      raw_description: formData.value.description,
      test_description: evaluatedPromptForLLM.value, // {idea} already swapped
      prompt_category_id: formData.value.prompt_category_id,
      base_prompt_id: formData.value.base_prompt_id || undefined,
      workspace_module_id: formData.value.workspace_module_id || undefined,
    };

    const res = await apiService.post('/common/prompts/preview', payload, { timeout: 600000 });

    const raw =
      res?.html ??
      res?.data?.html ??
      (typeof res === 'string' ? res : '') ?? '';

    const html = unwrapCodeFence(raw);
    responseHtml.value = html?.trim() ? html : '<em>No content returned.</em>';
    lastCheckedAt.value = new Date().toLocaleString();

    // re-render once more after response lands (if inputs changed while waiting)
    rerenderPreview();
  } catch (e: any) {
    responseHtml.value =
      `<div class="text-danger">Failed to generate response: ${e?.response?.data?.message || e?.message || 'Unknown error'}</div>`;
  } finally {
    isCheckingResponse.value = false;
  }
};

// ---------- Submit ----------
const handleSubmit = async () => {
  const id = route.query.id;
  const path = '/common/prompts';
  const payload: any = {
    ...formData.value,
    raw_description: formData.value.description,
    description: resolvedPrompt.value,
  };
  if (!payload.base_prompt_id) delete payload.base_prompt_id;
  if (!payload.workspace_module_id) delete payload.workspace_module_id;

  try {
    const result = isEdit.value
      ? await apiService.put(path, id, payload)
      : await apiService.post(path, payload);
    if (result) router.push('/prompts');
  } catch (error: any) {
    console.error('API Error:', error?.response?.data || error?.message);
  }
};

// ---------- Disable Save (response no longer required) ----------
const isFormInvalid = computed(() => {
  const f = formData.value;
  return !f.title.trim() || !f.prompt_category_id.trim() || !f.description.trim();
});

// ---------- Mount ----------
onMounted(async () => {
  await loadCategories();

  if (isEdit.value) {
    try {
      const id = route.query.id;
      const data = await apiService.get(`/common/prompts/${id}`);

      formData.value = {
        title: data.title || '',
        description: data.raw_description || data.description || '',
        prompt_category_id: data.prompt_category_id || '',
        base_prompt_id: data.base_prompt_id || '',
        workspace_module_id: data.workspace_module_id || '',
      };

      if (formData.value.prompt_category_id) {
        promptVariables.value =
          (await apiService.get(
            `/common/prompt-variables?prompt_category_id=${formData.value.prompt_category_id}`
          )) || [];
      }
    } catch (error: any) {
      console.error('❌ Failed to load prompt data:', error?.response || error);
    }
  }
});

return (_ctx: any,_cache: any) => {
  const _component_Breadcrumbs = _resolveComponent("Breadcrumbs")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_Breadcrumbs, {
      title: isEdit.value ? 'Edit Prompt' : 'Create Prompt',
      buttonLabel: 'Back',
      onButtonClick: createPrompt
    }, null, 8, ["title"]),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(BaseForm, {
            fields: formFields.value,
            modelValue: formData.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData).value = $event)),
            onSubmit: handleSubmit
          }, null, 8, ["fields", "modelValue"]),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("button", {
              class: "btn btn-outline-secondary",
              onClick: handleCheckResponse,
              disabled: !canCheckResponse.value || isCheckingResponse.value
            }, [
              (isCheckingResponse.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_6))
                : _createCommentVNode("", true),
              _cache[3] || (_cache[3] = _createTextVNode(" Check Response ", -1))
            ], 8, _hoisted_5),
            _createElementVNode("button", {
              class: "btn btn-primary",
              onClick: handleSubmit,
              disabled: isFormInvalid.value
            }, " Save Prompt ", 8, _hoisted_7)
          ]),
          (_openBlock(), _createElementBlock("div", {
            class: "alert alert-info mt-3",
            key: previewTick.value
          }, [
            _cache[4] || (_cache[4] = _createElementVNode("strong", null, "Preview:", -1)),
            _cache[5] || (_cache[5] = _createTextVNode("    ", -1)),
            _createElementVNode("p", { innerHTML: resolvedPrompt.value }, null, 8, _hoisted_8)
          ])),
          (responseHtml.value || isCheckingResponse.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _cache[6] || (_cache[6] = _createElementVNode("span", null, "Response", -1)),
                  (lastCheckedAt.value)
                    ? (_openBlock(), _createElementBlock("small", _hoisted_11, "Last checked: " + _toDisplayString(lastCheckedAt.value), 1))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_12, [
                  (isCheckingResponse.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_13, [...(_cache[7] || (_cache[7] = [
                        _createElementVNode("div", {
                          class: "spinner-border",
                          role: "status"
                        }, null, -1),
                        _createElementVNode("div", { class: "mt-2" }, "Generating response…", -1)
                      ]))]))
                    : (_openBlock(), _createElementBlock("div", {
                        key: 1,
                        innerHTML: responseHtml.value
                      }, null, 8, _hoisted_14))
                ])
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_15, [
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("div", _hoisted_17, [
              _cache[12] || (_cache[12] = _createElementVNode("h5", { class: "card-title mb-3" }, "Category", -1)),
              _withDirectives(_createElementVNode("select", {
                class: "form-select",
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.value.prompt_category_id) = $event)),
                "aria-label": "Select Category"
              }, [
                _cache[8] || (_cache[8] = _createElementVNode("option", {
                  disabled: "",
                  value: ""
                }, "-- Select Category --", -1)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categories.value, (c) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: c._id,
                    value: c._id
                  }, _toDisplayString(c.title), 9, _hoisted_18))
                }), 128))
              ], 512), [
                [_vModelSelect, formData.value.prompt_category_id]
              ]),
              _createElementVNode("div", _hoisted_19, [
                _createElementVNode("div", _hoisted_20, [
                  _cache[9] || (_cache[9] = _createElementVNode("h6", { class: "mb-0" }, "Variables", -1)),
                  (!formData.value.prompt_category_id)
                    ? (_openBlock(), _createElementBlock("small", _hoisted_21, " Select a category "))
                    : _createCommentVNode("", true)
                ]),
                (promptVariables.value.length)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(promptVariables.value, (v) => {
                        return (_openBlock(), _createElementBlock("span", {
                          key: v._id,
                          class: "badge bg-secondary mx-1 mb-1 cursor-pointer",
                          onClick: ($event: any) => (insertVariable(v.title))
                        }, _toDisplayString(v.title), 9, _hoisted_23))
                      }), 128))
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_24, "No variables to show."))
              ]),
              _createElementVNode("div", _hoisted_25, [
                _cache[10] || (_cache[10] = _createElementVNode("label", {
                  for: "test-idea",
                  class: "form-label"
                }, "Test Idea", -1)),
                _withDirectives(_createElementVNode("input", {
                  id: "test-idea",
                  type: "text",
                  class: "form-control",
                  placeholder: "Type an idea to replace {idea}",
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.value.idea) = $event))
                }, null, 512), [
                  [_vModelText, formData.value.idea]
                ]),
                _cache[11] || (_cache[11] = _createElementVNode("small", { class: "text-muted" }, [
                  _createTextVNode(" This replaces "),
                  _createElementVNode("code", null, "{idea}"),
                  _createTextVNode(" in the preview (and in the test request). ")
                ], -1))
              ])
            ])
          ]),
          _cache[13] || (_cache[13] = _createElementVNode("div", { class: "card mt-3" }, [
            _createElementVNode("div", { class: "card-body small text-muted" }, [
              _createTextVNode(" Click a variable to insert it into the Prompt at the cursor position."),
              _createElementVNode("br"),
              _createTextVNode(" The “Test Idea” box live-replaces "),
              _createElementVNode("code", null, "{idea}"),
              _createTextVNode(" in the preview. ")
            ])
          ], -1))
        ])
      ])
    ])
  ], 64))
}
}

})